<script setup lang="ts">
/**
 * ProductOffer Component
 *
 * Displays a single product offer with optional meal plan expansion.
 *
 * MULTI-ROOM SUPPORT:
 * - Single-room bookings: title = room name (OfferPage appends the board when the group
 *   has a single offer: "Room, Halbpension")
 * - Multi-room bookings: no title; each per-room heading from `roomDetails` gets
 *   `boardName` appended ("Room 1: Suite, Halbpension")
 * - Groups with several boards show no board in the headings; the boards are the meal options
 * - Both single-room and multi-room use the same template (unified flow approach)
 * - Single-room is treated as a special case of multi-room (1 room option)
 *
 * USAGE:
 * - OfferPage: Full offer details with room grouping and meal options
 * - CheckoutPage: Simple offers in underAccordion mode for upsells
 *
 * @see OfferPage.vue for production usage example
 * @see RoomCombinationGroup in services/mappers/offerMapper.ts for data structure
 */
import { ref, computed } from 'vue';
import BaseButton from './BaseButton.vue';
import BaseAlert from './BaseAlert.vue';
import RoomImageLightbox from './RoomImageLightbox.vue';
import { $t } from '@/composables/translations/useTranslate.ts';
import type { RoomTypeImage } from '@/types/schemas/nbc';
import type { RoomDetails } from '@/types/offer';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import { HIDE_REBATES_LISTING } from '@/constants/checkout/config';

interface MealOption {
  id: string;
  title: string;
  price: string;
  pricePrefix?: string;
  hasVamosBadge?: boolean;
  actionText?: string;
  discount?: number;
  discountReason?: string;
  oldPrice?: number;
  offerId?: number;
  isCheckingAvailability?: boolean;
  statusMessage?: string;
  statusType?: 'info' | 'success' | 'warning' | 'error';
}

interface Props {
  title: string;
  description: string;
  availability: 'available' | 'on-request' | 'unavailable';
  price: string;
  pricePrefix?: string;
  mealOptions?: MealOption[];
  expandable?: boolean;
  dateRange?: string;
  paxSummary?: string;
  discount?: number;
  discountReason?: string;
  oldPrice?: number;
  isCheckingAvailability?: boolean;
  errorMessage?: string;
  statusMessage?: string;
  statusType?: 'info' | 'success' | 'warning' | 'error';
  underAccordion?: boolean;
  wrapperClass?: string;
  roomImages?: RoomTypeImage[];
  htmlDescription?: string | null;
  roomDetails?: RoomDetails[];
  /**
   * Included board appended to each multi-room heading; the board is shared by all rooms.
   * Only set for a group with a single offer; with several boards they are the meal options.
   */
  boardName?: string;
  isSelected?: boolean;
  isLast?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  pricePrefix: 'ab',
  mealOptions: undefined,
  expandable: false,
  dateRange: undefined,
  paxSummary: undefined,
  discount: undefined,
  discountReason: undefined,
  oldPrice: undefined,
  isCheckingAvailability: false,
  errorMessage: undefined,
  statusMessage: undefined,
  statusType: undefined,
  underAccordion: false,
  wrapperClass: undefined,
  roomImages: undefined,
  htmlDescription: undefined,
  roomDetails: undefined,
  boardName: undefined,
  isSelected: false,
  isLast: false,
});

const emit = defineEmits<{
  'cta-click': [];
  'meal-option-click': [offerId: number];
}>();

const isExpanded = ref(false);
const isLightboxOpen = ref(false);
const lightboxInitialIndex = ref(0);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const openLightbox = (index: number) => {
  lightboxInitialIndex.value = index;
  isLightboxOpen.value = true;
};

const sanitizedHtmlDescription = computed(() => sanitizeHtml(props.htmlDescription));

/**
 * Fallback alt text for single-room images: the room name alone, since `title`
 * may carry the board as well ("Doppelzimmer, Halbpension").
 */
const roomImageAlt = computed(
  () =>
    props.roomDetails?.[0]?.nbc?.roomTypeName ||
    props.roomDetails?.[0]?.roomDescription ||
    props.title
);

const computedAvailabilityText = computed(() => {
  switch (props.availability) {
    case 'available':
      return $t('ibe.common.available', 'Available');
    case 'on-request':
      return $t('ibe.common.available-on-request', 'available on request');
    case 'unavailable':
      return $t('ibe.offer.fully-booked', 'Fully booked');
    default:
      return '';
  }
});

const availabilityColorClass = computed(() => {
  switch (props.availability) {
    case 'available':
      return 'bg-confirm';
    case 'on-request':
      return 'bg-text-highlight-3';
    case 'unavailable':
      return 'bg-error';
    default:
      return 'bg-gray-400';
  }
});

const isSingleOffer = computed(() => !props.expandable);

const displayMessage = computed(() => props.statusMessage || props.errorMessage);

const displayType = computed(() => props.statusType || (props.errorMessage ? 'error' : undefined));

const computedWrapperClass = computed(() => {
  return {
    [`bg-white border-highlight-1 ${props.wrapperClass}`]: true,
    [`border rounded-xl p-7.5 ${props.wrapperClass}`]: !props.underAccordion,
  };
});
</script>

<template>
  <div :class="computedWrapperClass">
    <div class="flex flex-col lg:flex-row">
      <div class="flex-1">
        <!-- VAMOS Badge -->
        <!--          <div v-if="hasVamosBadge" class="flex items-center gap-2.5 mb-2.5">-->
        <!--            <div class="w-[100px] h-5 bg-highlight-1"></div>-->
        <!--            <span class="text-body-large">Tipp</span>-->
        <!--          </div>-->
        <p
          v-if="dateRange || paxSummary"
          class="mb-2.5 flex flex-col items-start gap-1.5 xs:flex-row xs:items-center xs:gap-3"
        >
          <span v-if="dateRange" class="flex items-center gap-1.5">
            <i class="w-6 h-6 bg-black icon icon-calendar-blank"></i>
            <span>{{ dateRange }}</span>
          </span>
          <span v-if="paxSummary" class="flex items-center gap-1.5">
            <i class="w-6 h-6 bg-black icon icon-user"></i>
            <span>{{ paxSummary }}</span>
          </span>
        </p>

        <h3 v-if="title" class="text-body-large mb-2.5">{{ title }}</h3>

        <div
          v-if="sanitizedHtmlDescription"
          class="text-body text-gray-700 mb-3 leading-relaxed [&_p]:my-2 [&_ul]:my-2 [&_strong]:font-semibold"
          v-html="sanitizedHtmlDescription"
        ></div>

        <p v-else-if="description" class="text-body">{{ description }}</p>

        <div v-if="roomImages && roomImages.length > 0" class="mb-4">
          <div class="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
            <div
              v-for="(image, index) in roomImages"
              :key="index"
              class="relative w-10 h-10 flex-shrink-0 group cursor-pointer"
              @click="openLightbox(index)"
            >
              <img
                :src="image.thumbnail"
                :alt="image.caption || roomImageAlt"
                :title="image.caption || ''"
                class="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
              >
                <i class="w-5 h-5 bg-white icon icon-zoom"></i>
              </div>
            </div>
          </div>
        </div>

        <!-- Multi-room per-room details -->
        <div v-if="roomDetails && roomDetails.length > 1" class="space-y-8">
          <div v-for="(room, roomIndex) in roomDetails" :key="room.roomCode" class="space-y-3">
            <!-- Room number and name -->
            <h4 class="text-body-large font-semibold">
              {{ $t('ibe.offer.room-number', 'Room {number}:', { number: roomIndex + 1 }) }}
              {{ room.nbc?.roomTypeName || room.roomDescription
              }}<template v-if="boardName">, {{ boardName }}</template>
            </h4>

            <div
              v-if="room.nbc?.description"
              class="text-body text-gray-700 leading-relaxed [&_p]:my-2 [&_ul]:my-2 [&_strong]:font-semibold"
              v-html="sanitizeHtml(room.nbc.description)"
            ></div>

            <div
              v-if="room.nbc?.images && room.nbc.images.length > 0"
              class="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300"
            >
              <div
                v-for="(image, imgIndex) in room.nbc.images"
                :key="imgIndex"
                class="relative w-10 h-10 flex-shrink-0 group cursor-pointer"
                @click="openLightbox(imgIndex)"
              >
                <img
                  :src="image.thumbnail"
                  :alt="image.caption || room.roomDescription"
                  :title="image.caption || ''"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
                >
                  <i class="w-5 h-5 bg-white icon icon-zoom"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col items-start lg:items-end gap-2.5 lg:w-1/3 mt-6 mb-4 lg:mt-0 lg:mb-0"
      >
        <div class="flex gap-1.5 items-center">
          <div
            :class="['w-2.5 h-2.5 rounded-full relative top-[-2px]', availabilityColorClass]"
          ></div>
          <p class="text-body-small">{{ computedAvailabilityText }}</p>
        </div>

        <VTooltip v-if="!HIDE_REBATES_LISTING && discount && discountReason" placement="top">
          <div class="flex items-center gap-1 px-2 py-1 bg-text-highlight-2 cursor-help">
            <p class="text-white text-body-small">
              {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
              <strong>{{ discount }}%</strong>
            </p>
          </div>

          <template #popper>
            <p>{{ discountReason }}</p>
          </template>
        </VTooltip>
        <div
          v-else-if="!HIDE_REBATES_LISTING && discount"
          class="flex items-center gap-1 px-2 py-1 bg-text-highlight-2"
        >
          <p class="text-white text-body-small">
            {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
            <strong>{{ discount }}%</strong>
          </p>
        </div>

        <div class="flex gap-1.5 items-baseline">
          <p class="text-body-small">{{ pricePrefix }}</p>
          <p class="text-price">{{ price }}</p>
        </div>

        <BaseButton
          v-if="isSingleOffer && availability !== 'unavailable'"
          :variant="isSelected ? 'secondary' : 'primary'"
          class="mt-2.5"
          :loading="isCheckingAvailability"
          @click="emit('cta-click')"
        >
          {{
            isSelected ? $t('ibe.aux.selected', 'Selected') : $t('ibe.aux.select-cta', 'Select now')
          }}
        </BaseButton>
      </div>
    </div>

    <div class="flex justify-end">
      <BaseButton
        v-if="expandable && availability !== 'unavailable'"
        variant="secondary"
        :aria-expanded="isExpanded"
        aria-controls="meal-options"
        :icon-right="isExpanded ? 'icon-chevron-up-thin' : 'icon-chevron-down-thin'"
        @click="toggleExpanded"
      >
        {{ $t('ibe.offer.select-meal-plan', 'Select meal plan') }}
      </BaseButton>
    </div>

    <BaseAlert
      v-if="displayMessage && (!expandable || availability === 'unavailable')"
      :type="displayType"
      :message="displayMessage"
      class="mt-5"
    />

    <div
      v-if="expandable && isExpanded && mealOptions && availability !== 'unavailable'"
      id="meal-options"
      class="pt-5 pl-12 mt-5 space-y-px"
    >
      <div
        v-for="(option, index) in mealOptions"
        :key="option.id"
        :class="{ 'border-t border-gray-200 pt-5': index > 0 }"
        class="pb-5 last:pb-0"
      >
        <div class="flex flex-col lg:flex-row">
          <div class="flex-1">
            <div v-if="option.title" class="flex items-center gap-2.5">
              <!--                <div v-if="option.hasVamosBadge" class="flex items-center gap-2.5">-->
              <!--                  <div class="w-[100px] h-5 bg-highlight-1"></div>-->
              <!--                  <span class="text-body-large">Tipp</span>-->
              <!--                </div>-->
              <h4 v-if="!option.hasVamosBadge" class="text-body-large">{{ option.title }}</h4>
            </div>
            <h4 v-if="option.hasVamosBadge && option.title" class="text-body-large">
              {{ option.title }}
            </h4>
          </div>

          <div class="flex flex-col items-start lg:items-end gap-2.5 mt-4 lg:mt-0 lg:ml-5">
            <VTooltip
              v-if="!HIDE_REBATES_LISTING && option.discount && option.discountReason"
              placement="top"
            >
              <div class="flex items-center gap-1 px-2 py-1 bg-text-highlight-2 cursor-help">
                <p class="text-white text-body-small">
                  {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
                  <strong>{{ option.discount }}%</strong>
                </p>
              </div>

              <template #popper>
                <p>{{ option.discountReason }}</p>
              </template>
            </VTooltip>
            <div
              v-else-if="!HIDE_REBATES_LISTING && option.discount"
              class="flex items-center gap-1 px-2 py-1 bg-text-highlight-2"
            >
              <p class="text-white text-body-small">
                {{ $t('ibe.checkout.discount.badge-prefix', 'up to') }}
                <strong>{{ option.discount }}%</strong>
              </p>
            </div>

            <div class="flex gap-1.5 items-baseline">
              <p class="text-body-small">
                {{ option.pricePrefix || $t('ibe.common.from', 'from') }}
              </p>
              <p class="text-price">{{ option.price }}</p>
            </div>

            <BaseButton
              variant="primary"
              :loading="option.isCheckingAvailability"
              @click="option.offerId && emit('meal-option-click', option.offerId)"
            >
              {{ option.actionText || $t('ibe.aux.select-cta', 'Select now') }}
            </BaseButton>
          </div>
        </div>

        <BaseAlert
          v-if="option.statusMessage"
          :type="option.statusType"
          :message="option.statusMessage"
          class="mt-2.5"
        />
      </div>
    </div>

    <!-- Room Image Lightbox -->
    <RoomImageLightbox
      v-if="roomImages && roomImages.length > 0"
      v-model:open="isLightboxOpen"
      :images="roomImages"
      :initial-index="lightboxInitialIndex"
    />
  </div>
</template>
